import { SnapshotStore } from "./snapshotStore";

export type ConnectionStatus = "online" | "offline" | "reconnecting";

export interface ConnectionSnapshot {
  status: ConnectionStatus;
  /** Связь вернулась и данные перечитаны: сообщение об этом видно несколько секунд. */
  restored: boolean;
  /** Пока связи не было, действие человека не ушло на сервер. */
  rejectedWrite: boolean;
}

export interface ConnectionMonitorOptions {
  /** Перечитывает с сервера всё, что показано на экране. Вызывается при возврате связи. */
  resync: () => Promise<void>;
  target?: Pick<Window, "addEventListener" | "removeEventListener">;
  isOnline?: () => boolean;
  /** Сколько показывать сообщение «данные обновлены». */
  restoredMs?: number;
}

/**
 * Состояние сети для интерфейса: онлайн, нет связи, восстанавливаем.
 *
 * Браузерное событие `online` говорит лишь о том, что появился канал, а не о
 * том, что данные на экране верны: пока связи не было, на сервере могло
 * измениться что угодно, включая права и сессию. Поэтому между «нет связи» и
 * «онлайн» есть отдельное состояние, в котором приложение перечитывает данные.
 */
export class ConnectionMonitor extends SnapshotStore<ConnectionSnapshot> {
  readonly #options: Required<Omit<ConnectionMonitorOptions, "resync">> &
    Pick<ConnectionMonitorOptions, "resync">;
  #generation = 0;
  #restoredTimer: ReturnType<typeof setTimeout> | undefined;

  constructor(options: ConnectionMonitorOptions) {
    const resolved = {
      target: window,
      isOnline: () => navigator.onLine,
      restoredMs: 4000,
      ...options,
    };
    super({
      status: resolved.isOnline() ? "online" : "offline",
      restored: false,
      rejectedWrite: false,
    });
    this.#options = resolved;
  }

  start(): () => void {
    const { target } = this.#options;
    const goOffline = () => this.#goOffline();
    const goOnline = () => void this.#goOnline();
    target.addEventListener("offline", goOffline);
    target.addEventListener("online", goOnline);
    // Страница могла открыться без сети (из кеша оболочки) — событий не будет.
    this.update({
      status: this.#options.isOnline() ? this.current.status : "offline",
    });

    return () => {
      target.removeEventListener("offline", goOffline);
      target.removeEventListener("online", goOnline);
      clearTimeout(this.#restoredTimer);
    };
  }

  /** Запись не дошла до сервера из-за сети: человеку нужно сказать, что она не сохранена. */
  reportRejectedWrite(): void {
    if (this.current.rejectedWrite) return;
    this.update({ rejectedWrite: true });
  }

  dismissRejectedWrite(): void {
    this.update({ rejectedWrite: false });
  }

  #goOffline(): void {
    this.#generation += 1;
    clearTimeout(this.#restoredTimer);
    this.update({ status: "offline", restored: false });
  }

  async #goOnline(): Promise<void> {
    this.#generation += 1;
    const generation = this.#generation;
    clearTimeout(this.#restoredTimer);
    this.update({ status: "reconnecting", restored: false });
    try {
      await this.#options.resync();
    } catch {
      // Часть запросов могла не выполниться — экраны покажут свои ошибки.
      // Связь при этом есть, и оставлять «восстанавливаем» навсегда нельзя.
    }
    // Пока перечитывали, связь могла пропасть снова — это уже другое состояние.
    if (generation !== this.#generation) return;
    this.update({ status: "online", restored: true });
    this.#restoredTimer = setTimeout(
      () => this.update({ restored: false, rejectedWrite: false }),
      this.#options.restoredMs,
    );
  }
}
